import { useState, useEffect, useRef } from "react";
import { API, axios } from "@/api";
import { Calendar, Plus, ChevronDown, ChevronUp, Users, Percent } from "lucide-react";
import { toast } from "sonner";

export default function FoodHolidayPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ holiday_id: "", name: "", emoji: "", date_type: "fixed", month: 1, day: 1, week_number: 1, weekday: 0, matching_cuisine_tags: "", description: "", all_vendors: false });

  useEffect(() => {
    axios.get(`${API}/admin/food-holidays`).then(r => setData(r.data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    try {
      const payload = {
        ...form,
        month: parseInt(form.month),
        day: form.date_type === "fixed" ? parseInt(form.day) : null,
        week_number: form.date_type === "dynamic" ? parseInt(form.week_number) : null,
        weekday: form.date_type === "dynamic" ? parseInt(form.weekday) : null,
        matching_cuisine_tags: form.matching_cuisine_tags ? form.matching_cuisine_tags.split(",").map(t => t.trim()) : [],
      };
      await axios.post(`${API}/admin/food-holidays`, payload);
      toast.success("Holiday added!");
      setShowForm(false);
      setForm({ holiday_id: "", name: "", emoji: "", date_type: "fixed", month: 1, day: 1, week_number: 1, weekday: 0, matching_cuisine_tags: "", description: "", all_vendors: false });
      // Refresh
      const r = await axios.get(`${API}/admin/food-holidays`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  if (loading) return <div className="py-8 text-center text-sm text-slate-400">Loading food holidays...</div>;

  return (
    <div className="space-y-4" data-testid="food-holiday-admin-panel">
      {/* Upcoming holidays with participation */}
      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#F59E0B]" />
          Upcoming Food Holidays (30 days)
        </h3>
        {(data?.upcoming || []).length === 0 ? (
          <p className="text-xs text-slate-400">No food holidays in the next 30 days</p>
        ) : (
          <div className="space-y-2">
            {(data?.upcoming || []).map(h => (
              <div key={h.holiday_id} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2.5" data-testid={`admin-holiday-${h.holiday_id}`}>
                <span className="text-xl">{h.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-700">{h.name}</p>
                  <p className="text-[10px] text-slate-400">{h.date} &middot; {h.days_until}d away</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-bold text-slate-700">{h.active_campaigns}/{h.matching_vendors}</p>
                  <p className="text-[9px] text-slate-400">{h.participation_pct}% participating</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top holidays by campaigns */}
      {(data?.top_holidays_by_campaigns || []).length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <h3 className="text-sm font-bold text-slate-800 mb-3">Top Holidays by Participation</h3>
          <div className="space-y-1.5">
            {data.top_holidays_by_campaigns.map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span>{t.emoji}</span>
                <span className="text-slate-700 flex-1">{t.name}</span>
                <span className="font-bold text-slate-800">{t.campaigns} campaigns</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* City breakdown */}
      {(data?.city_breakdown || []).length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <h3 className="text-sm font-bold text-slate-800 mb-3">City Breakdown</h3>
          <div className="space-y-1.5">
            {data.city_breakdown.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-slate-700">{c.city}</span>
                <span className="font-bold text-slate-800">{c.campaigns} campaigns</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add new holiday form */}
      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 text-sm font-bold text-slate-800 w-full" data-testid="add-holiday-toggle">
          <Plus className="w-4 h-4 text-[#F59E0B]" />
          Add New Food Holiday
          {showForm ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
        </button>
        {showForm && (
          <div className="mt-3 space-y-2" data-testid="add-holiday-form">
            <div className="grid grid-cols-2 gap-2">
              <input value={form.holiday_id} onChange={e => setForm(f => ({...f, holiday_id: e.target.value}))} placeholder="holiday_id (e.g. national_xyz_day)" className="col-span-2 text-xs border border-slate-200 rounded px-2 py-1.5" />
              <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Name" className="text-xs border border-slate-200 rounded px-2 py-1.5" />
              <input value={form.emoji} onChange={e => setForm(f => ({...f, emoji: e.target.value}))} placeholder="Emoji" className="text-xs border border-slate-200 rounded px-2 py-1.5" />
              <select value={form.date_type} onChange={e => setForm(f => ({...f, date_type: e.target.value}))} className="text-xs border border-slate-200 rounded px-2 py-1.5">
                <option value="fixed">Fixed Date</option>
                <option value="dynamic">Dynamic (nth weekday)</option>
              </select>
              <input type="number" value={form.month} onChange={e => setForm(f => ({...f, month: e.target.value}))} placeholder="Month (1-12)" className="text-xs border border-slate-200 rounded px-2 py-1.5" min="1" max="12" />
              {form.date_type === "fixed" ? (
                <input type="number" value={form.day} onChange={e => setForm(f => ({...f, day: e.target.value}))} placeholder="Day (1-31)" className="text-xs border border-slate-200 rounded px-2 py-1.5" min="1" max="31" />
              ) : (
                <>
                  <input type="number" value={form.week_number} onChange={e => setForm(f => ({...f, week_number: e.target.value}))} placeholder="Week # (1-5)" className="text-xs border border-slate-200 rounded px-2 py-1.5" min="1" max="5" />
                  <select value={form.weekday} onChange={e => setForm(f => ({...f, weekday: e.target.value}))} className="text-xs border border-slate-200 rounded px-2 py-1.5">
                    {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d,i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </>
              )}
              <input value={form.matching_cuisine_tags} onChange={e => setForm(f => ({...f, matching_cuisine_tags: e.target.value}))} placeholder="Tags (comma sep)" className="col-span-2 text-xs border border-slate-200 rounded px-2 py-1.5" />
              <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Description (one sentence)" className="col-span-2 text-xs border border-slate-200 rounded px-2 py-1.5" />
              <label className="col-span-2 flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={form.all_vendors} onChange={e => setForm(f => ({...f, all_vendors: e.target.checked}))} />
                All vendors qualify (regardless of cuisine)
              </label>
            </div>
            <button onClick={handleAdd} className="w-full py-2 rounded-lg bg-[#F59E0B] text-[#1C1C1E] text-xs font-bold active:scale-[0.97] transition" data-testid="add-holiday-submit">
              Add Holiday
            </button>
          </div>
        )}
      </div>

      <p className="text-[10px] text-slate-400 text-center">Total holidays in system: {data?.total_holidays || 0}</p>
    </div>
  );
}
